import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { CustomerAuthModal } from "@/components/customer/customer-auth-modal";
import { useCustomer } from "@/lib/customer-context";
import { CreditCard, Wallet, Shield, Zap } from "lucide-react";

export default function PortalLogin() {
  const [, setLocation] = useLocation();
  const { customer, isLoading, refreshCustomer } = useCustomer();
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (customer && !isLoading) {
      setLocation("/portal/home");
    }
  }, [customer, isLoading, setLocation]);

  // Auto-open auth modal
  useEffect(() => {
    if (!customer && !isLoading) {
      setShowAuthModal(true);
    }
  }, [customer, isLoading]);

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <div className="flex min-h-screen">
        {/* Left side - Branding */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary to-primary/80 p-12 flex-col justify-between">
          <div>
            <div className="mb-8">
              <img src="/vedalogic-logo.svg" alt="VedaLogic" className="h-16 w-auto object-contain" />
            </div>
            
            <div className="space-y-6 mt-16">
              <h2 className="text-4xl font-bold text-white leading-tight">
                Manage your virtual cards with ease
              </h2>
              <p className="text-lg text-white/90 max-w-md">
                Secure payments, instant top-ups, and complete control over your spending.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="p-4 rounded-xl bg-white/10 backdrop-blur-sm">
              <Shield className="h-8 w-8 text-white mb-2" />
              <h3 className="text-white font-semibold mb-1">Secure</h3>
              <p className="text-sm text-white/80">Bank-level encryption</p>
            </div>
            <div className="p-4 rounded-xl bg-white/10 backdrop-blur-sm">
              <Zap className="h-8 w-8 text-white mb-2" />
              <h3 className="text-white font-semibold mb-1">Instant</h3>
              <p className="text-sm text-white/80">Real-time updates</p>
            </div>
            <div className="p-4 rounded-xl bg-white/10 backdrop-blur-sm">
              <Wallet className="h-8 w-8 text-white mb-2" />
              <h3 className="text-white font-semibold mb-1">Wallet</h3>
              <p className="text-sm text-white/80">Easy top-ups via UPI</p>
            </div>
            <div className="p-4 rounded-xl bg-white/10 backdrop-blur-sm">
              <CreditCard className="h-8 w-8 text-white mb-2" />
              <h3 className="text-white font-semibold mb-1">Cards</h3>
              <p className="text-sm text-white/80">Manage all your cards</p>
            </div>
          </div>
        </div>

        {/* Right side - Login */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <img src="/vedalogic-logo.svg" alt="VedaLogic" className="h-14 w-auto mx-auto mb-4 object-contain brightness-0 dark:brightness-100" />
              <h1 className="text-3xl font-bold mb-2">Welcome Back</h1>
              <p className="text-muted-foreground">
                Sign in to access your virtual cards
              </p>
            </div>

            <div className="bg-card border rounded-xl p-8 shadow-sm">
              <button
                onClick={() => setShowAuthModal(true)}
                className="w-full h-12 px-6 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium"
              >
                Sign In with Phone
              </button>

              <p className="text-center text-sm text-muted-foreground mt-6">
                New to VedaLogic? You'll be automatically registered on first sign-in.
              </p>
            </div>

            <p className="text-center text-xs text-muted-foreground mt-8">
              By signing in, you agree to our Terms of Service and <a href="/privacy-policy" className="underline hover:text-foreground transition-colors">Privacy Policy</a>
            </p>
            <div className="text-center mt-3">
              <a href="https://vedalogic.betteruptime.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                System Status
              </a>
            </div>
          </div>
        </div>
      </div>

      <CustomerAuthModal
        open={showAuthModal}
        onOpenChange={setShowAuthModal}
        onSuccess={async (customer) => {
          setShowAuthModal(false);
          // Refresh the customer context so ProtectedPortalRoute sees the new session
          // before we redirect, otherwise it would bounce back to /portal/login.
          await refreshCustomer();
          setLocation("/portal/home");
        }}
      />
    </div>
  );
}
